// S4.1 — THE CONTROL POINT LIBRARY, /quality/control-points (spec 07 §4.1).
//
// The definition set, browsable: code, name, stage, what it is bound to, how it
// samples, whether it blocks, which version is current. It is NOT paginated,
// because §5 says the collection is not — a bounded definition set the floor
// PWA must hold WHOLE to execute an inspection offline. A "Show more" here
// would be a control the server does not offer.
//
// VERSIONS ARE VISIBLE, NOT HIDDEN. `is_current=true` is the default filter and
// the version number rides every row, because §3.1 step 5's edit-as-new-version
// is the module's central mechanic and a library that showed only names would
// leave an inspector unable to answer "which version did that inspection run?".
// The superseded chain is one filter away rather than one query away.
//
// THE EMPTY STATE HAS BOTH CTAs §4.1 asks for — "Seed the cable pack or create
// one" — and the seed one is gated on `quality.vertical_pack.seed`, which is
// group_admin ALONE (§7). A facility_admin sees the create CTA and no seed
// button: absence, not a disabled control whose reason nobody can read (D-118).

import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { isQualityReadRefused, useQualityPermissions } from "../hooks/permissions";
import { localeText } from "../run/inspection-run";
import {
  useDeactivateQualityControlPoint,
  useQualityControlPoints,
  useSeedQualityVerticalPack,
  type ControlPointsQuery,
  type QualityControlPoint,
} from "../hooks/quality-queries";

export interface ControlPointsSearch {
  stage?: string;
  source?: string;
  active?: string;
  is_current?: string;
}

export function validateControlPointsSearch(search: Record<string, unknown>): ControlPointsSearch {
  const out: ControlPointsSearch = {};
  for (const key of ["stage", "source", "active", "is_current"] as const) {
    const value = search[key];
    if (typeof value === "string" && value.length > 0) out[key] = value;
  }
  return out;
}

/** §4.1's "bindings summary" cell — "PVC pellet · 2 suppliers". Rendered from
 * the SHAPE of the bindings rather than from resolved names: this slice has no
 * supplier module to resolve against (D-123 §0.1) and module 01's product names
 * would be N extra reads on a definition screen. Counting is honest and does
 * not pretend to a name it cannot get. */
export function bindingSummary(point: QualityControlPoint, t: TFunction): string {
  if (point.stage === "in_process") return t("quality.library.boundOnRouting");
  if (point.bindings.length === 0) return t("quality.library.boundNothing");
  const materials = point.bindings.filter((b) => b.material_id !== null).length;
  const products = point.bindings.filter((b) => b.product_id !== null).length;
  const suppliers = point.bindings.filter((b) => b.supplier_id !== null).length;
  const parts: string[] = [];
  if (materials > 0) parts.push(t("quality.library.boundMaterials", { count: materials }));
  if (products > 0) parts.push(t("quality.library.boundProducts", { count: products }));
  if (suppliers > 0) parts.push(t("quality.library.boundSuppliers", { count: suppliers }));
  return parts.length > 0 ? parts.join(" · ") : t("quality.library.boundNothing");
}

/** §4.1's "sample rule" cell — the §2.1 mode with its parameter, because
 * "percentage" without the percentage is not a rule anybody can check. */
export function sampleSummary(point: QualityControlPoint, t: TFunction): string {
  switch (point.sample_mode) {
    case "fixed_count":
      return t("quality.sample.fixed_count", { n: point.sample_size ?? 0 });
    case "first_n":
      return t("quality.sample.first_n", { n: point.sample_size ?? 0 });
    case "percentage":
      return t("quality.sample.percentage", { pct: point.sample_pct ?? "" });
    case "aql":
      return t("quality.sample.aql", {
        level: point.aql_inspection_level ?? "",
        value: point.aql_value ?? "",
      });
    default:
      return t("quality.sample.every_unit");
  }
}

export function ControlPointsScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useQualityPermissions();

  const [stage, setStage] = useState("");
  const [source, setSource] = useState("");
  const [active, setActive] = useState("true");
  const [current, setCurrent] = useState("true");
  const [q, setQ] = useState("");
  const [seedReport, setSeedReport] = useState<string | undefined>(undefined);

  const query: ControlPointsQuery = useMemo(
    () => ({
      ...(stage.length > 0 ? { stage: stage as NonNullable<ControlPointsQuery["stage"]> } : {}),
      ...(source.length > 0 ? { source: source as NonNullable<ControlPointsQuery["source"]> } : {}),
      ...(active.length > 0 ? { active: active as "true" | "false" } : {}),
      ...(current.length > 0 ? { is_current: current as "true" | "false" } : {}),
      ...(q.trim().length > 0 ? { q: q.trim() } : {}),
    }),
    [stage, source, active, current, q],
  );

  const refused = isQualityReadRefused(perms, "quality.catalog.read");
  const points = useQualityControlPoints(query, !refused);
  const canManage = perms.has("quality.catalog.manage");
  const canSeed = perms.has("quality.vertical_pack.seed");
  const deactivate = useDeactivateQualityControlPoint();
  const seed = useSeedQualityVerticalPack();

  const rows = points.data ?? [];

  const runSeed = (): void => {
    seed.mutate("cable-wire", {
      onSuccess: (data) => {
        // §9.15 — seeding is idempotent per code, so the answer has TWO halves
        // and reporting one total would hide the difference between "installed
        // six points" and "found six points already yours and touched none".
        setSeedReport(
          t("quality.library.seedReport", {
            created: data.control_points_created,
            skipped: data.control_points_skipped,
            causes: data.failure_causes_created,
          }),
        );
      },
    });
  };

  const columns: DataTableColumn<QualityControlPoint>[] = [
    { key: "code", header: t("quality.library.colCode"), id: true },
    {
      key: "name",
      header: t("quality.library.colName"),
      // The BUNDLE, resolved for the reader's language (#128) — never the flat
      // `name`, which is only ever the English. A qc lead browsing the library
      // in Russian reads the Russian the pack shipped; a point with no Russian
      // falls back to `en` and then to the code, which is at least addressable.
      render: (row) => localeText(row.name_i18n, i18n.language, row.code),
    },
    {
      key: "stage",
      header: t("quality.library.colStage"),
      render: (row) => (
        <StatusChip variant={row.stage === "final" ? "accent" : "neutral"} dot={false}>
          {t(`quality.stage.${row.stage}`)}
        </StatusChip>
      ),
    },
    {
      key: "bindings",
      header: t("quality.library.colBindings"),
      render: (row) => bindingSummary(row, t),
    },
    {
      key: "sample",
      header: t("quality.library.colSample"),
      render: (row) => sampleSummary(row, t),
    },
    {
      key: "flags",
      header: t("quality.library.colFlags"),
      render: (row) => (
        <>
          {row.is_blocking ? (
            <StatusChip variant="warn" data-testid="quality-chip-blocking">
              {t("quality.library.blocking")}
            </StatusChip>
          ) : null}
          {row.source === "vertical_pack" ? (
            <StatusChip variant="neutral" dot={false} data-testid="quality-chip-pack">
              {t("quality.library.fromPack")}
            </StatusChip>
          ) : null}
          {!row.active ? (
            <StatusChip variant="neutral" data-testid="quality-chip-inactive">
              {t("quality.library.inactive")}
            </StatusChip>
          ) : null}
        </>
      ),
    },
    {
      key: "version",
      header: t("quality.library.colVersion"),
      numeric: true,
      render: (row) => (
        <span data-testid="quality-cp-version">
          {t("quality.library.versionCell", { version: row.version })}
          {!row.is_current ? ` · ${t("quality.library.superseded")}` : ""}
        </span>
      ),
    },
    {
      key: "actions",
      header: t("quality.library.colActions"),
      actions: true,
      render: (row) =>
        // D-118: a role without `quality.catalog.manage` gets NO action cell
        // content at all — not two greyed links. The row is still readable,
        // which is what a planner opened it for.
        canManage && row.is_current ? (
          <div className="quality-rowacts">
            <Button
              size="sm"
              data-testid="quality-cp-edit"
              onClick={() => {
                void navigate({
                  to: "/quality/control-points/$id/edit",
                  params: { id: row.id },
                });
              }}
            >
              {t("quality.library.edit")}
            </Button>
            <Button
              size="sm"
              data-testid="quality-cp-duplicate"
              onClick={() => {
                void navigate({
                  to: "/quality/control-points/new",
                  search: { from: row.id },
                });
              }}
            >
              {t("quality.library.duplicate")}
            </Button>
            {row.active ? (
              <Button
                size="sm"
                data-testid="quality-cp-deactivate"
                onClick={() => {
                  deactivate.mutate(row.id);
                }}
              >
                {t("quality.library.deactivate")}
              </Button>
            ) : null}
          </div>
        ) : null,
    },
  ];

  return (
    <section className="quality-library" data-testid="quality-library">
      <PageHeader
        title={t("quality.library.title")}
        actions={
          <>
            {canManage ? (
              <Button
                variant="primary"
                size="sm"
                data-testid="quality-cp-new"
                onClick={() => {
                  void navigate({ to: "/quality/control-points/new", search: {} });
                }}
              >
                {t("quality.library.newPoint")}
              </Button>
            ) : null}
          </>
        }
      />

      <div className="filter-bar" data-testid="quality-library-filters">
        <Field label={t("quality.library.filterStage")}>
          <select
            value={stage}
            data-testid="quality-filter-stage"
            onChange={(e) => {
              setStage(e.target.value);
            }}
          >
            <option value="">{t("quality.library.allStages")}</option>
            {(["receiving", "in_process", "final"] as const).map((value) => (
              <option key={value} value={value}>
                {t(`quality.stage.${value}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("quality.library.filterSource")}>
          <select
            value={source}
            data-testid="quality-filter-source"
            onChange={(e) => {
              setSource(e.target.value);
            }}
          >
            <option value="">{t("quality.library.allSources")}</option>
            <option value="vertical_pack">{t("quality.library.fromPack")}</option>
            <option value="custom">{t("quality.library.custom")}</option>
          </select>
        </Field>
        <Field label={t("quality.library.filterActive")}>
          <select
            value={active}
            data-testid="quality-filter-active"
            onChange={(e) => {
              setActive(e.target.value);
            }}
          >
            <option value="">{t("quality.library.anyActive")}</option>
            <option value="true">{t("quality.library.onlyActive")}</option>
            <option value="false">{t("quality.library.onlyInactive")}</option>
          </select>
        </Field>
        {/* The superseded chain §3.1 step 5 leaves behind. A library that could
            not show it would make "which version ran?" unanswerable in the UI. */}
        <Field label={t("quality.library.filterVersions")}>
          <select
            value={current}
            data-testid="quality-filter-current"
            onChange={(e) => {
              setCurrent(e.target.value);
            }}
          >
            <option value="true">{t("quality.library.onlyCurrent")}</option>
            <option value="">{t("quality.library.allVersions")}</option>
          </select>
        </Field>
        <Field label={t("quality.library.filterSearch")} wide>
          <input
            value={q}
            data-testid="quality-filter-q"
            placeholder={t("quality.library.searchHint")}
            onChange={(e) => {
              setQ(e.target.value);
            }}
          />
        </Field>
      </div>

      {refused ? (
        <ForbiddenState title={t("quality.access.title")} body={t("quality.access.library")} />
      ) : null}

      {/* PAUSED, not slow: react-query holds a query while the browser reports
          no network and never times out, so a skeleton here would shimmer for
          as long as the cut lasts and promise nothing. */}
      {!refused && points.fetchStatus === "paused" && rows.length === 0 ? (
        <OfflineState title={t("quality.offline.title")} body={t("quality.offline.body")} />
      ) : null}

      {!refused && points.isPending && points.fetchStatus !== "paused" ? (
        <TableSkeleton rows={5} cols={columns.length} label={t("quality.common.loading")} />
      ) : null}

      {!refused && points.error !== null && !(points.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("quality.library.error")}
          retryLabel={t("quality.common.retry")}
          requestIdLabel={t("quality.common.requestId")}
          requestId={requestIdOf(points.error)}
          onRetry={() => {
            void points.refetch();
          }}
        />
      ) : null}

      {seedReport !== undefined ? (
        <p className="quality-note" role="status" data-testid="quality-seed-report">
          {seedReport}
        </p>
      ) : null}
      {seed.isError ? (
        <p className="quality-error-note" role="alert" data-testid="quality-seed-failed">
          {t("quality.library.seedFailed")}
        </p>
      ) : null}
      {deactivate.isError ? (
        <p className="quality-error-note" role="alert" data-testid="quality-deactivate-failed">
          {t("quality.library.deactivateFailed")}
        </p>
      ) : null}

      {!refused &&
      !points.isPending &&
      points.fetchStatus !== "paused" &&
      points.error === null &&
      rows.length === 0 ? (
        <div data-testid="quality-library-empty">
          <EmptyState
            title={t("quality.library.emptyTitle")}
            body={t("quality.library.emptyBody")}
            {...(canManage
              ? {
                  ctaLabel: t("quality.library.newPoint"),
                  onCta: () => {
                    void navigate({ to: "/quality/control-points/new", search: {} });
                  },
                }
              : {})}
          />
          {canSeed ? (
            <div className="quality-toolbar">
              <Button
                data-testid="quality-seed-pack"
                busy={seed.isPending}
                busyLabel={t("quality.common.saving")}
                onClick={runSeed}
              >
                {t("quality.library.seedPack")}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {rows.length > 0 ? (
        <DataTable
          data-testid="quality-cp-table"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          empty={null}
          caption={t("quality.library.tableCaption")}
          rowTestId="quality-cp-row"
        />
      ) : null}
    </section>
  );
}
